import { type FC, useEffect, useRef, useState } from "react";
import {
  PhoneInput,
  type PhoneInputValue,
  phoneInputValueFromE164,
  phoneInputValueToE164,
} from "@visioni/a3s-ui/components";
import "@visioni/a3s-ui/theme";

type CountryCode = PhoneInputValue["country"];

export interface NotificationContactPhoneFieldProps {
  /** `null` if the user has never set one. */
  readonly phoneNumber: string | null;
  /**
   * Called with a validated E.164 phone number, or `null` to clear — not called if the field is unchanged.
   * Return the save's promise so a failed save can be retried with the same number.
   */
  readonly onSave: (phoneNumber: string | null) => void | Promise<void>;
  /** Country selected while no number is on file — the user's region. Defaults to Australia. */
  readonly defaultCountry?: CountryCode | undefined;
  /** Not editable — e.g. while the number on file is still being refreshed. */
  readonly disabled?: boolean | undefined;
}

/** The regions A3S serves, listed first in the country picker. */
const PINNED_COUNTRIES: readonly CountryCode[] = ["AU", "NZ"];

const INVALID_NUMBER_ERROR = "Enter a valid mobile number for the selected country.";
const INVALID_ON_FILE_ERROR = "The number on file isn't valid. Re-enter it to receive SMS.";

/**
 * A number saved before per-country checks existed may not be valid. Say so up
 * front, since SMS to it will never arrive; it's left as it is (leaving the
 * field unedited doesn't save) until the user changes it.
 */
function onFileError(stored: string | null, defaultCountry: CountryCode): string | undefined {
  if (stored === null) return undefined;
  return phoneInputValueToE164(phoneInputValueFromE164(stored, defaultCountry)) === undefined ? INVALID_ON_FILE_ERROR
    : undefined;
}

/**
 * SMS delivery reads only from this field, never from the account's Auth0
 * profile (which is essentially never populated for this tenant base) — see
 * `NotificationContactInfoService`. A user with no phone number on file can
 * still receive email notifications; SMS for any category just has nothing
 * to send to until this is set.
 *
 * Saves on blur of the number, and immediately when the country changes —
 * see `handleChange` for when a focused number waits for its blur instead.
 */
export const NotificationContactPhoneField: FC<NotificationContactPhoneFieldProps> = ({
  phoneNumber,
  onSave,
  defaultCountry = "AU",
  disabled = false,
}) => {
  const [value, setValue] = useState(() => phoneInputValueFromE164(phoneNumber, defaultCountry));
  const [error, setError] = useState<string | undefined>(() => onFileError(phoneNumber, defaultCountry));
  // Refs, not state: reading focus/clear status must not itself trigger the
  // resync effect below (a plain `isFocused` state dependency would re-run
  // the effect on every blur, snapping the field back to the last-known
  // `phoneNumber` before the just-triggered save has round-tripped).
  const isFocusedRef = useRef(false);
  const justClearedRef = useRef(false);
  // What was last handed to `onSave`, or `undefined` once that save failed.
  // `phoneNumber` only catches up once a save round-trips, so comparing
  // against it alone let a country change and the blur after it both send
  // the same number.
  const lastSentRef = useRef<string | null | undefined>(phoneNumber);

  // `phoneNumber` typically arrives asynchronously (a still-in-flight query
  // when this mounts, or a save round-tripping) — resync whenever it
  // changes, but never while the user is actively focused in the field, so
  // an in-progress edit can't be clobbered by a background refetch.
  useEffect(() => {
    lastSentRef.current = phoneNumber;
    if (isFocusedRef.current) return;
    // No number keeps the selected country: clearing the number shouldn't also
    // undo the country the user picked once that clear has saved. (A first load
    // with no number already starts on `defaultCountry`.)
    setValue((current) =>
      phoneNumber === null ?
        { country: current.country, nationalNumber: "" }
      : phoneInputValueFromE164(phoneNumber, defaultCountry),
    );
    setError(onFileError(phoneNumber, defaultCountry));
  }, [phoneNumber, defaultCountry]);

  const send = (next: string | null) => {
    if (next === lastSentRef.current) return;
    lastSentRef.current = next;
    // A failed save mustn't count as sent, or retrying the same number — which
    // the page's error toast asks for — would be skipped as a duplicate.
    void Promise.resolve(onSave(next)).catch(() => {
      if (lastSentRef.current === next) lastSentRef.current = undefined;
    });
  };

  const save = (next: PhoneInputValue) => {
    const stored = lastSentRef.current === undefined ? phoneNumber : lastSentRef.current;
    const saved = phoneInputValueFromE164(stored, defaultCountry);
    const typed = next.nationalNumber.trim();
    // Compared as split rather than as E.164 so an unedited number on file
    // that this field can't validate (saved before it checked per-country
    // rules) is left alone rather than rejected as a fresh entry — but still
    // gets its on-file warning back, which editing it had cleared.
    if (typed === saved.nationalNumber && (typed === "" || next.country === saved.country)) {
      setError(onFileError(stored, defaultCountry));
      return;
    }

    if (typed === "") {
      setError(undefined);
      send(null);
      return;
    }

    const e164 = phoneInputValueToE164(next);
    if (e164 === undefined) {
      setError(INVALID_NUMBER_ERROR);
      return;
    }

    setError(undefined);
    send(e164);
  };

  const handleChange = (next: PhoneInputValue) => {
    const countryChanged = next.country !== value.country;
    setValue(next);
    if (error !== undefined) setError(undefined);
    if (!countryChanged) return;
    // While the number is focused a blur will normally follow and save — but
    // not reliably: on native, tapping the flag needn't blur the number, and
    // leaving the screen while it's focused fires no blur at all. So a number
    // that's already complete in the new country saves now; an incomplete one
    // waits for the blur, rather than erroring at someone still typing.
    if (!isFocusedRef.current || phoneInputValueToE164(next) !== undefined) save(next);
  };

  const handleBlur = () => {
    isFocusedRef.current = false;
    const current = value;

    // On web, pressing the clear button blurs the input *before* the button's
    // own press handler runs — deferring this by a tick lets handleClear's
    // synchronous flag win the race, so a clear-then-blur sequence saves
    // `null` exactly once rather than both the pre-clear text and `null`.
    setTimeout(() => {
      if (justClearedRef.current) {
        justClearedRef.current = false;
        return;
      }
      save(current);
    }, 0);
  };

  const handleClear = () => {
    justClearedRef.current = true;
    setValue((v) => ({ ...v, nationalNumber: "" }));
    setError(undefined);
    send(null);

    // On platforms/paths where clear doesn't also blur the input (e.g.
    // native, where tapping the clear icon doesn't dismiss the keyboard),
    // there's no handleBlur timeout to consume this flag. Left set, it
    // would linger and cause some later, unrelated blur (after the user
    // types a new number) to wrongly skip its save. Reset it on the same
    // tick handleBlur's own timeout uses, so it only ever survives long
    // enough to coalesce a blur that's actually part of this clear.
    setTimeout(() => {
      justClearedRef.current = false;
    }, 0);
  };

  return (
    <PhoneInput
      label="Phone number for SMS"
      placeholder="Mobile number"
      value={value}
      onChange={handleChange}
      pinnedCountries={PINNED_COUNTRIES}
      onFocus={() => {
        isFocusedRef.current = true;
      }}
      onBlur={handleBlur}
      onClear={handleClear}
      error={error}
      helperText={error === undefined ? "Required to receive SMS notifications." : undefined}
      clearable
      disabled={disabled}
      accessibilityLabel="Phone number for SMS notifications"
    />
  );
};
